import moment from 'moment';
import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Row, Col, Card } from 'react-bootstrap';
import { Route, Routes, useParams } from 'react-router-dom';
import { can } from '../../../common/Util';
import { Icon, LoadingAnimation } from '../../../common/components/Utilities';
import SicknessResource from '../../../common/resource/SicknessResource';

function Show() {
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState(null);
  const [payTypes, setPayTypes] = useState(null);
  const [sickness, setSickness] = useState({});
  const { id } = useParams();

  const load = async () => {
    setLoading(true);
    setTypes(await SicknessResource.types());
    setPayTypes(await SicknessResource.payTypes());
    setSickness(await SicknessResource.get(id));
    setLoading(false);
  };

  const reload = async () => {
    setLoading(true);
    setSickness(await SicknessResource.get(id));
    setLoading(false);
  };

  const formatDate = (date) => (date ? moment(date).format('YYYY-MM-DD') : '');
  const handleEndDateChange = (event) => {
    const { value } = event.currentTarget;
    setSickness((current) => ({ ...current, end_date: value }));
  };

  useEffect(() => { load(); }, [id]);

  return (
    <Row>
      <Col md={4}>
        <Card>
          <Card.Header>
            Sickness Information
          </Card.Header>
          <Card.Body>
            <form action={`/sickness/${id}`} method="POST">
              <input type="hidden" name="_method" value="PUT" />
              <input type="hidden" name="_token" value={window.props.csrfToken} />

              <div className="form-group">
                <label htmlFor="start_date">Start Date</label>
                <input
                  className="form-control"
                  name="start_date"
                  id="start_date"
                  type="date"
                  value={formatDate(sickness.start_date)}
                  readOnly
                />
              </div>

              <div className="form-group">
                <label htmlFor="end_date">End Date</label>
                <input
                  className="form-control"
                  name="end_date"
                  id="end_date"
                  type="date"
                  value={formatDate(sickness.end_date)}
                  onChange={handleEndDateChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="type_id">Reason</label>
                {types !== null && typeof sickness.type_id !== 'undefined' && (
                  <select id="type_id" name="type_id" className="form-control" defaultValue={sickness.type_id}>
                    <option value="">Not Selected / Unknown</option>
                    {types.map((type) => (
                      <option key={type.id} value={type.id}>{type.title}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea className="form-control" id="notes" name="notes" defaultValue={sickness.notes} />
                <div className="form-text small text-danger">
                  <i className="fa fa-exclamation-triangle mr-1" />
                  It is important that you ask for and document a reason for sickness absence.
                  Answers such as &quot;sick&quot; or
                  &quot;not feeling well today&quot; are not appropriate.
                </div>
              </div>

              <button type="submit" className="btn btn-outline-success">Update</button>
            </form>
          </Card.Body>
        </Card>

        {can('userAdmin') && (
          <div className="mt-4">
            <form action={`/sickness/${sickness.id}`} method="POST">
              <input type="hidden" name="_method" value="DELETE" />
              <input type="hidden" name="_token" value={window.props.csrfToken} />
              <button
                type="submit"
                className="btn btn-danger"
                disabled={typeof sickness.days === 'undefined' || sickness.days.length > 0}
              >
                <Icon icon="trash" append="mr-2" />
                Delete Sickness
              </button>
            </form>
          </div>
        )}
      </Col>

      <Col>
        <Card>
          <Card.Header>Sick Days</Card.Header>
          <LoadingAnimation loading={loading} />
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Duration</th>
                <th>Pay</th>
                <th width={1} />
              </tr>
            </thead>
            <tbody>
              {typeof sickness.days !== 'undefined' && payTypes !== null && sickness.days.map((day) => (
                <SicknessDay key={day.id} sickDay={day} payTypes={payTypes} reload={reload} />
              ))}
            </tbody>
          </table>
        </Card>
      </Col>
    </Row>
  );
}

function SicknessDay({ sickDay, payTypes, reload }) {
  const [loading, setLoading] = useState(false);
  const [day, setDay] = useState(sickDay);
  const [selectVersion, setSelectVersion] = useState(0);

  const deleteDay = async (event) => {
    event.preventDefault();

    if (window.confirm('Are you sure you want to delete this day?')) {
      setLoading(true);
      await SicknessResource.dayDelete(day.id);
      reload();
      setLoading(false);
    }
  };

  const updateDuration = async (event) => {
    const { value } = event.currentTarget;
    setLoading(true);
    try {
      setDay(await SicknessResource.dayPut(day.id, { duration: value }));
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'sickness-day', action: 'update-duration' } });
      setSelectVersion((current) => current + 1);
    } finally {
      setLoading(false);
    }
  };

  const updatePay = async (event) => {
    const { value } = event.currentTarget;
    setLoading(true);
    try {
      setDay(await SicknessResource.dayPut(day.id, { pay_type_id: value }));
    } catch (error) {
      Sentry.captureException(error, { tags: { area: 'sickness-day', action: 'update-pay-type' } });
      setSelectVersion((current) => current + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <tr key={day.id}>
      <td>
        {moment(day.sickness_date).format('dddd, D MMM YYYY')}
      </td>
      <td>
        <select key={`duration-${selectVersion}`} className="form-control" defaultValue={day.duration} onChange={updateDuration} readOnly={loading}>
          <option value={1}>Full Day</option>
          <option value={0.5}>Half Day</option>
        </select>
      </td>
      <td>
        <select key={`pay-${selectVersion}`} className="form-control" defaultValue={day.pay_type_id} onChange={updatePay} readOnly={loading}>
          {payTypes.map((type) => (
            <option key={type.id} value={type.id}>{type.title}</option>
          ))}
        </select>
      </td>
      <td>
        <button type="button" className="btn btn-outline-danger" onClick={deleteDay}>
          <Icon icon="trash" variant="fad" />
        </button>
      </td>
    </tr>
  );
}

export default function () {
  return (
    <Routes>
      <Route path=":id" element={<Show />} />
    </Routes>
  );
}
